import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "399",
  "slug": "transcontinental-countries-on-two-continents",
  "title": "The Countries That Sit on Two Continents",
  "excerpt": "A handful of countries straddle the line between two continents, from Istanbul's two halves to Russia's vast Europe-to-Asia sprawl. Here is where the borders fall.",
  "date": "2026-06-10",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1589561454226-796a8aa89b05?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxpc3RhbmJ1bCUyMGJvc3Bob3J1cyUyMHN0cmFpdHxlbnwxfDB8fHwxNzgxMDc5MjIwfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "We tend to file each country neatly under a single continent. But continents are a human invention, and a handful of countries refuse to stay in one box, sprawling across the boundary between two of them. These transcontinental countries are some of the most interesting places on the map, precisely because they sit on a seam."
    },
    {
      "type": "heading",
      "text": "Where Europe Meets Asia"
    },
    {
      "type": "paragraph",
      "text": "Most transcontinental countries cluster along the long, fuzzy line dividing Europe from Asia. Russia is the giant of the group: the Ural Mountains are usually taken as the divide, putting the historic heartland around Moscow in Europe and the vast majority of the country's land area in Asia. Kazakhstan straddles the Ural River, with a slice of territory west of it counted as European. Georgia and Azerbaijan, tucked into the Caucasus, are likewise split between the two continents depending on exactly where you draw the line."
    },
    {
      "type": "heading",
      "text": "A City Split in Two: Turkey"
    },
    {
      "type": "paragraph",
      "text": "Turkey offers the most vivid image of a continental divide. The narrow Bosphorus strait runs right through Istanbul, so the city, and the country, has one foot in Europe and one in Asia. You can take a ferry from a European neighbourhood to an Asian one in minutes. Most of Turkey's land lies in Asia, on the peninsula of Anatolia, while a smaller European portion sits to the west of the strait."
    },
    {
      "type": "heading",
      "text": "The African Country With a Foot in Asia: Egypt"
    },
    {
      "type": "paragraph",
      "text": "Egypt is overwhelmingly an African country, but the Sinai Peninsula, east of the Suez Canal, sits on the Asian side of the conventional boundary. That makes Egypt a bridge between Africa and Asia, which is a large part of why the Suez region has been so strategically prized for so long."
    },
    {
      "type": "heading",
      "text": "The Tricky Cases"
    },
    {
      "type": "paragraph",
      "text": "Beyond the clear examples, things get blurry. Indonesia spreads across the line that biogeographers draw between Asia and Oceania, with its western islands tied to Asia and its eastern reaches grading into the Australian realm. Panama is sometimes described as a bridge between North and South America, though geographers usually place the whole country in North America and put the continental boundary at the Colombian border. These debates are a reminder that the lines are drawn by people, not by nature."
    },
    {
      "type": "heading",
      "text": "Why Continents Are Fuzzier Than They Look"
    },
    {
      "type": "paragraph",
      "text": "There is no law of physics that says where one continent ends and another begins. The Europe-Asia divide in particular is a cultural and historical convention more than a geological fact, since the two share a single enormous landmass often called Eurasia. Transcontinental countries simply sit where those human conventions happen to cut across real political borders."
    },
    {
      "type": "list",
      "items": [
        "Russia and Kazakhstan: split by the Urals and the Ural River",
        "Turkey: divided by the Bosphorus, with Istanbul on both sides",
        "Egypt: mostly African, with the Sinai Peninsula in Asia",
        "Georgia and Azerbaijan: straddling the Caucasus boundary",
        "Indonesia and Panama: debated cases on continental seams"
      ]
    }
  ]
};
